import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { logErrorToSentry } from '../utils/sentryErrorLogger';

const isChunkLoadError = (error) => {
	return (
		error?.name === 'ChunkLoadError' ||
		error?.message?.includes('Loading chunk') ||
		error?.message?.includes('Failed to fetch dynamically imported module') ||
		error?.code === 'CHUNK_LOAD_ERROR'
	);
};

const useFunnelComponent = (componentName) => {
	const { funnelId } = useParams();
	const [Component, setComponent] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const isMountedRef = useRef(true);
	const reloadAttemptedRef = useRef(false);

	useEffect(() => {
		isMountedRef.current = true;
		reloadAttemptedRef.current = false;
		setComponent(null);
		setLoading(true);
		setError(null);

		const loadComponent = async () => {
			// Try funnel-specific component first
			try {
				const funnelModule = await import(
					/* webpackChunkName: "funnel-[request]" */
					`../pages/funnels/funnel${funnelId}/${componentName}.jsx`
				);

				if (isMountedRef.current && funnelModule?.default) {
					setComponent(() => funnelModule.default);
					setLoading(false);
					return;
				}
			} catch (funnelError) {
				// Try default funnel component before considering reloads
				try {
					const defaultModule = await import(
						/* webpackChunkName: "default-funnel-[request]" */
						`../pages/funnels/defaultFunnel/${componentName}.jsx`
					);

					if (isMountedRef.current && defaultModule?.default) {
						setComponent(() => defaultModule.default);
						setLoading(false);
						return;
					}
				} catch (defaultError) {
					// If both funnel-specific and default imports failed due to chunk errors, attempt a single reload
					if (
						isChunkLoadError(defaultError) &&
						!reloadAttemptedRef.current
					) {
						reloadAttemptedRef.current = true;
						logErrorToSentry(defaultError, {
							component: 'FunnelComponent',
							tags: { funnelId, componentName, stage: 'chunk_load_error_default', action: 'reloading' },
							extra: { funnelId, componentName, funnelError: funnelError?.message },
							errorType: 'chunk_loading',
						});
						window.location.reload();
						return;
					}

					// Both failed - log and set error (fallback message differentiates chunk errors)
					logErrorToSentry(defaultError, {
						component: 'FunnelComponent',
						tags: { funnelId, componentName, stage: 'both_failed' },
						extra: {
							funnelId,
							componentName,
							funnelError: funnelError?.message,
							defaultError: defaultError?.message,
							isChunkError: isChunkLoadError(defaultError),
							reloadAttempted: reloadAttemptedRef.current,
						},
						errorType: isChunkLoadError(defaultError) ? 'chunk_loading' : 'module_not_found',
					});

					if (isMountedRef.current) {
						const message = isChunkLoadError(defaultError)
							? 'Unable to load page. Please refresh your browser.'
							: 'Error loading page, please refresh the page';
						setError(new Error(message));
						setLoading(false);
					}
				}
			}
		};

		loadComponent();

		return () => {
			isMountedRef.current = false;
		};
	}, [funnelId, componentName]);

	return { Component, loading, error };
};

export default useFunnelComponent;